// src/pages/LegalDocumentContent.jsx
import React from "react";

export default function LegalDocumentContent({ documentText }) {
  const { preamble, sections } = parseLegalDocument(documentText);

  return (
    <div>
      {preamble.length > 0 && (
        <div className="mb-9 rounded-2xl border border-[#FFE1CF] bg-[#FFF7ED] p-5 md:p-6">
          <div className="space-y-2 text-base md:text-lg leading-relaxed text-[#4B5563]">
            {preamble.map((line, index) => (
              <p
                key={`${line}-${index}`}
                className={
                  index === 0
                    ? "font-bold text-[#333333] text-lg md:text-xl"
                    : ""
                }
              >
                {line}
              </p>
            ))}
          </div>
        </div>
      )}

      {sections.map((section) => (
        <Section key={section.title} title={section.title}>
          <ContentBlocks lines={section.lines} />
        </Section>
      ))}
    </div>
  );
}

function parseLegalDocument(text) {
  const lines = text
    .split("\n")
    .map((line) => line.trim())
    .filter((line, index, array) => {
      const previous = array[index - 1];
      return line !== "" || previous !== "";
    });

  const firstSectionIndex = lines.findIndex((line) => isMainSectionTitle(line));

  const preamble =
    firstSectionIndex === -1
      ? lines.filter(Boolean)
      : lines.slice(0, firstSectionIndex).filter(Boolean);

  const sectionLines =
    firstSectionIndex === -1 ? [] : lines.slice(firstSectionIndex);

  const sections = [];
  let currentSection = null;

  sectionLines.forEach((line) => {
    if (!line) {
      if (currentSection) currentSection.lines.push("");
      return;
    }

    if (isMainSectionTitle(line)) {
      if (currentSection) sections.push(currentSection);

      currentSection = {
        title: line,
        lines: [],
      };

      return;
    }

    if (currentSection) {
      currentSection.lines.push(line);
    }
  });

  if (currentSection) sections.push(currentSection);

  return { preamble, sections };
}

function isMainSectionTitle(line) {
  return /^\d+\.\s+/.test(line);
}

function isSubsectionTitle(line) {
  return /^\d+\.\d+\s+/.test(line);
}

function ContentBlocks({ lines }) {
  const blocks = createBlocks(lines);

  return (
    <>
      {blocks.map((block, index) => {
        if (block.type === "subheading") {
          return (
            <h4
              key={`${block.text}-${index}`}
              className="mt-6 mb-3 text-lg md:text-xl font-bold text-[#333333]"
            >
              {block.text}
            </h4>
          );
        }

        if (block.type === "list") {
          return (
            <div key={`${block.heading}-${index}`} className="mb-5 last:mb-0">
              {block.heading && <p className="mb-2">{block.heading}</p>}

              <ul className="list-disc pl-6 space-y-2 marker:text-[#FF914D]">
                {block.items.map((item, itemIndex) => (
                  <li key={`${item}-${itemIndex}`}>{item}</li>
                ))}
              </ul>
            </div>
          );
        }

        return (
          <div key={`${block.text}-${index}`} className="mb-5 last:mb-0">
            {block.lines.map((line, lineIndex) => (
              <p key={`${line}-${lineIndex}`} className="mb-3 last:mb-0">
                {line}
              </p>
            ))}
          </div>
        );
      })}
    </>
  );
}

function createBlocks(lines) {
  const blocks = [];
  let paragraphLines = [];

  const flushParagraph = () => {
    if (paragraphLines.length > 0) {
      blocks.push({
        type: "paragraph",
        lines: paragraphLines,
      });
      paragraphLines = [];
    }
  };

  for (let i = 0; i < lines.length; i += 1) {
    const line = lines[i];

    if (!line) {
      flushParagraph();
      continue;
    }

    if (isSubsectionTitle(line)) {
      flushParagraph();
      blocks.push({
        type: "subheading",
        text: line,
      });
      continue;
    }

    if (line.endsWith(":")) {
      const items = [];
      let j = i + 1;

      while (
        j < lines.length &&
        lines[j] &&
        !isMainSectionTitle(lines[j]) &&
        !isSubsectionTitle(lines[j]) &&
        !lines[j].endsWith(":")
      ) {
        items.push(cleanBullet(lines[j]));
        j += 1;
      }

      if (items.length > 0) {
        flushParagraph();

        blocks.push({
          type: "list",
          heading: line,
          items,
        });

        i = j - 1;
        continue;
      }
    }

    paragraphLines.push(line);
  }

  flushParagraph();

  return blocks;
}

function cleanBullet(line) {
  return line.replace(/^[-•]\s*/, "");
}

function Section({ title, children }) {
  return (
    <section className="mb-10 last:mb-0">
      <h3 className="text-2xl md:text-3xl font-bold mb-4 text-[#333333]">
        {title}
      </h3>

      <div className="text-base md:text-lg leading-relaxed text-[#4B5563]">
        {children}
      </div>
    </section>
  );
}